/**
 * Server component for header static structure.
 * Pre-renders header container and logo on the server
 * for better LCP performance. Interactive parts are handled by client component.
 */

import React from 'react';
import { Logo } from '@/components/logo';
import { HeaderClient } from './header';

/**
 * Server component that pre-renders the static header structure.
 * The interactive parts (auth, navigation, user menu) are handled by the client component.
 *
 * @returns Server-rendered header structure with logo and client component slot
 *
 * @example
 * ```tsx
 * <HeaderServer />
 * ```
 *
 * @see {@link ./header.tsx} for the client component implementation
 */
export function HeaderServer() {
  return (
    <header className="relative sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <div className="container flex h-14 items-center px-4 sm:px-6">
        {/* Logo - pre-rendered on server. Shown on mobile only; on md+ the left rail carries it. */}
        <div className="mr-2 flex md:hidden">
          <Logo href="/dashboard" size="sm" />
        </div>
        {/* Client component handles navigation and interactive parts */}
        <HeaderClient />
      </div>
    </header>
  );
}
